import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  MonitorCheck,
  Info,
  Save,
  Loader2,
  AtSign,
  Globe,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { base_url } from "../../config";

const ExhibitorAccessInstructions = () => (
  <section className="rounded-2xl border border-slate-700/40 bg-slate-800/40 overflow-hidden">
    <header className="flex items-center gap-2 px-4 py-2 border-b border-slate-700/40 bg-slate-900/40">
      <Info className="h-4 w-4 text-slate-400" />
      <h2 className="text-[11px] uppercase tracking-wider font-semibold text-slate-300">
        How to grant exhibitor access
      </h2>
    </header>
    <div className="p-4 text-sm space-y-2 text-slate-300">
      <p className="text-slate-400">
        Enter email addresses or domains, one per line, to grant exhibitor
        registration access:
      </p>
      <ul className="space-y-1.5">
        <li className="flex items-center gap-2">
          <Globe className="h-3.5 w-3.5 text-blue-400 shrink-0" />
          <span>Whole domain:</span>
          <code className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-slate-700/40 text-blue-200 border border-blue-500/30">
            @example.com
          </code>
        </li>
        <li className="flex items-center gap-2">
          <AtSign className="h-3.5 w-3.5 text-violet-400 shrink-0" />
          <span>Single user:</span>
          <code className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-slate-700/40 text-violet-200 border border-violet-500/30">
            user@example.com
          </code>
        </li>
      </ul>
    </div>
  </section>
);

export default function Exhibitors() {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "ok" | "error">("idle");
  const [saveMessage, setSaveMessage] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${base_url}/api/admin/exhibitors`)
      .then((response) => setText(response.data.text))
      .catch((error) => console.error("Error fetching exhibitors:", error))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus("idle");
    setSaveMessage("");
    try {
      await axios.post(`${base_url}/api/admin/exhibitors`, { text });
      setSaveStatus("ok");
      setSaveMessage("Sponsors list updated successfully.");
    } catch (error) {
      setSaveStatus("error");
      setSaveMessage("Error updating sponsors list.");
    } finally {
      setSaving(false);
    }
  };

  const entryCount = text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean).length;

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-[1100px] px-4 py-5 space-y-4">
        <header className="flex items-center justify-between">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <MonitorCheck className="h-5 w-5 text-violet-400" />
            Exhibitors
            <span className="text-xs font-normal text-slate-400">
              ({entryCount} {entryCount === 1 ? "entry" : "entries"})
            </span>
          </h1>
        </header>

        <ExhibitorAccessInstructions />

        <section className="rounded-2xl border border-slate-700/40 bg-slate-800/40 overflow-hidden">
          <header className="flex items-center gap-2 px-4 py-2 border-b border-slate-700/40 bg-slate-900/40">
            <AtSign className="h-4 w-4 text-slate-400" />
            <h2 className="text-[11px] uppercase tracking-wider font-semibold text-slate-300">
              Allowlist
            </h2>
          </header>
          <div className="p-4">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-6 w-6 text-blue-400 animate-spin" />
              </div>
            ) : (
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={15}
                spellCheck={false}
                className="w-full font-mono text-xs bg-slate-900 border border-slate-700/60 rounded-md px-3 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/30 min-h-[260px] resize-y"
                placeholder="@example.com&#10;user@example.com"
              />
            )}
          </div>
          <footer className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-700/40 bg-slate-900/40">
            <div className="text-xs">
              {saveStatus === "ok" && (
                <span className="inline-flex items-center gap-1.5 text-emerald-300">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {saveMessage}
                </span>
              )}
              {saveStatus === "error" && (
                <span className="inline-flex items-center gap-1.5 text-rose-300">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {saveMessage}
                </span>
              )}
            </div>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-violet-500/20 text-violet-200 border border-violet-500/30 hover:bg-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              {saving ? "Saving…" : "Update sponsor domains"}
            </button>
          </footer>
        </section>
      </div>
    </div>
  );
}
